import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { TreeBudgetCategorySelect } from '../components/ui/tree-budget-category-select';
import { 
  Target, Plus, RefreshCw, Edit, Trash2, Eye, TrendingUp, 
  TrendingDown, AlertTriangle, CheckCircle2, Calendar, Building2,
  ShoppingCart, Store, Users, DollarSign, Lock
} from 'lucide-react';

const PresupuestosPage = () => {
  const { empresaActual } = useApp();
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('GASTOS');
  const [categorias, setCategorias] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [presupuestoDetalle, setPresupuestoDetalle] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form
  const [form, setForm] = useState({
    nombre: '',
    tipo: 'GASTOS',
    periodo: 'MENSUAL',
    fecha_inicio: '',
    fecha_fin: '',
    alerta_porcentaje: 80,
    bloquear_exceso: false,
    notas: '',
    lineas: []
  });

  const formatMoney = (amount) => {
    return parseFloat(amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 });
  };

  // Cargar datos maestros
  useEffect(() => {
    const cargarMaestros = async () => {
      if (!empresaActual?.id) return;
      try {
        const [catRes, ccRes] = await Promise.all([
          api.get('/categorias', { params: { empresa_id: empresaActual.id } }),
          api.get('/centros-costo', { params: { empresa_id: empresaActual.id } })
        ]);
        setCategorias(catRes.data);
        setCentrosCosto(ccRes.data);
        
        // Obtener tiendas y vendedores únicos de ventas
        const ventasRes = await api.get('/ventas', { params: { empresa_id: empresaActual.id, limit: 1000 } });
        const ventasData = ventasRes.data?.ventas || ventasRes.data || [];
        const tiendasUnicas = [...new Set(ventasData.filter(v => v.tienda_name).map(v => v.tienda_name))];
        const vendedoresUnicos = [...new Set(ventasData.filter(v => v.vendedor_name).map(v => v.vendedor_name))];
        setTiendas(tiendasUnicas);
        setVendedores(vendedoresUnicos);
      } catch (error) {
        console.error('Error cargando maestros:', error);
      }
    };
    cargarMaestros();
  }, [empresaActual?.id]);

  // Cargar presupuestos
  const cargarPresupuestos = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/presupuestos', { 
        params: { empresa_id: empresaActual.id, tipo: activeTab } 
      });
      setPresupuestos(res.data);
    } catch (error) {
      toast.error('Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, activeTab]);

  useEffect(() => {
    cargarPresupuestos();
  }, [cargarPresupuestos]);

  // Abrir modal nuevo
  const abrirNuevo = () => {
    const hoy = new Date();
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    
    setEditando(null);
    setForm({
      nombre: '',
      tipo: activeTab,
      periodo: 'MENSUAL',
      fecha_inicio: primerDia.toISOString().split('T')[0],
      fecha_fin: ultimoDia.toISOString().split('T')[0],
      alerta_porcentaje: 80,
      bloquear_exceso: false,
      notas: '',
      lineas: []
    });
    setModalOpen(true);
  };

  // Abrir modal editar
  const abrirEditar = async (presupuesto) => {
    try {
      const res = await api.get(`/presupuestos/${presupuesto.id}`);
      setEditando(res.data);
      setForm({
        nombre: res.data.nombre,
        tipo: res.data.tipo,
        periodo: res.data.periodo,
        fecha_inicio: res.data.fecha_inicio,
        fecha_fin: res.data.fecha_fin,
        alerta_porcentaje: res.data.alerta_porcentaje,
        bloquear_exceso: res.data.bloquear_exceso,
        notas: res.data.notas || '',
        lineas: res.data.lineas || []
      });
      setModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar presupuesto');
    }
  };

  // Ver detalle
  const verDetalle = async (presupuesto) => {
    try {
      const res = await api.get(`/presupuestos/${presupuesto.id}`);
      setPresupuestoDetalle(res.data);
      setDetalleOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  // Agregar línea
  const agregarLinea = () => {
    setForm({
      ...form,
      lineas: [...form.lineas, {
        categoria_id: null,
        centro_costo_id: null,
        tienda_name: '',
        vendedor_name: '',
        monto_presupuestado: 0,
        descripcion: ''
      }]
    });
  };

  // Actualizar línea - accepts single field or multiple fields object
  const actualizarLinea = (index, fieldOrFields, value) => {
    const nuevasLineas = [...form.lineas];
    if (typeof fieldOrFields === 'object') {
      // Multiple fields update
      nuevasLineas[index] = { ...nuevasLineas[index], ...fieldOrFields };
    } else {
      // Single field update
      nuevasLineas[index] = { ...nuevasLineas[index], [fieldOrFields]: value };
    }
    setForm({ ...form, lineas: nuevasLineas });
  };

  // Eliminar línea
  const eliminarLinea = (index) => {
    setForm({ ...form, lineas: form.lineas.filter((_, i) => i !== index) });
  };

  // Guardar presupuesto
  const guardarPresupuesto = async () => {
    if (!form.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (form.lineas.length === 0) {
      toast.error('Agrega al menos una línea de presupuesto');
      return;
    }

    try {
      const data = { ...form, empresa_id: empresaActual.id };
      
      if (editando) {
        await api.put(`/presupuestos/${editando.id}`, data);
        toast.success('Presupuesto actualizado');
      } else {
        await api.post('/presupuestos', data);
        toast.success('Presupuesto creado');
      }
      setModalOpen(false);
      cargarPresupuestos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // Cambiar estado
  const cambiarEstado = async (id, estado) => {
    try {
      await api.put(`/presupuestos/${id}/estado`, { estado });
      toast.success(`Presupuesto ${estado.toLowerCase()}`);
      cargarPresupuestos();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // Eliminar presupuesto
  const eliminarPresupuesto = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/presupuestos/${deleteConfirm.id}`);
      toast.success('Presupuesto eliminado');
      setDeleteConfirm(null);
      cargarPresupuestos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Calcular total líneas
  const totalLineas = form.lineas.reduce((sum, l) => sum + parseFloat(l.monto_presupuestado || 0), 0);

  // Badge estado
  const getEstadoBadge = (estado) => {
    const styles = {
      'BORRADOR': 'bg-gray-100 text-gray-700',
      'ACTIVO': 'bg-green-100 text-green-700',
      'CERRADO': 'bg-blue-100 text-blue-700'
    };
    return <Badge className={styles[estado] || 'bg-gray-100'}>{estado}</Badge>;
  };

  if (!empresaActual) {
    return <div className="p-8 text-center text-gray-500">Selecciona una empresa</div>;
  }

  return (
    <div className="space-y-6" data-testid="presupuestos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold">Presupuestos</h1>
            <p className="text-sm text-gray-500">Control de gastos y metas de ventas</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cargarPresupuestos} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={abrirNuevo} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Presupuesto
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="GASTOS" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Gastos / Egresos
          </TabsTrigger>
          <TabsTrigger value="VENTAS" className="flex items-center gap-2">
            <Store className="w-4 h-4" />
            Ventas / Metas
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Fechas</TableHead>
                    <TableHead className="text-right">Presupuestado</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Alertas</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                      </TableCell>
                    </TableRow>
                  ) : presupuestos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                        <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        No hay presupuestos de {activeTab.toLowerCase()}
                      </TableCell>
                    </TableRow>
                  ) : presupuestos.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.nombre}</TableCell>
                      <TableCell><Badge variant="outline">{p.periodo}</Badge></TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {p.fecha_inicio} a {p.fecha_fin}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        S/. {formatMoney(p.monto_total)}
                      </TableCell>
                      <TableCell>{getEstadoBadge(p.estado)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          {p.alerta_porcentaje}%
                          {p.bloquear_exceso && <Lock className="w-3 h-3 text-red-500" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => verDetalle(p)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => abrirEditar(p)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {p.estado === 'BORRADOR' && (
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => cambiarEstado(p.id, 'ACTIVO')}>
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                          {p.estado === 'ACTIVO' && (
                            <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => cambiarEstado(p.id, 'CERRADO')}>
                              <Lock className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteConfirm(p)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Crear/Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              {editando ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
            </DialogTitle>
            <DialogDescription>
              {form.tipo === 'GASTOS' ? 'Define límites de gastos por categoría' : 'Define metas de ventas'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} placeholder="Ej: Presupuesto Enero 2026" className="mt-1" />
              </div>
              <div>
                <Label>Período</Label>
                <Select value={form.periodo} onValueChange={(v) => setForm({...form, periodo: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MENSUAL">Mensual</SelectItem>
                    <SelectItem value="TRIMESTRAL">Trimestral</SelectItem>
                    <SelectItem value="ANUAL">Anual</SelectItem>
                    <SelectItem value="PERSONALIZADO">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Inicio</Label>
                <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({...form, fecha_inicio: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Fecha Fin</Label>
                <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({...form, fecha_fin: e.target.value})} className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Alerta al (%)</Label>
                <Input type="number" value={form.alerta_porcentaje} onChange={(e) => setForm({...form, alerta_porcentaje: parseInt(e.target.value) || 80})} className="mt-1" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.bloquear_exceso} onCheckedChange={(v) => setForm({...form, bloquear_exceso: v})} />
                <Label>Bloquear si excede</Label>
              </div>
            </div>

            {/* Líneas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Líneas de Presupuesto</Label>
                <Button size="sm" variant="outline" onClick={agregarLinea}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar Línea
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{form.tipo === 'GASTOS' ? 'Categoría / Centro Costo' : 'Tienda / Vendedor'}</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.lineas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-4 text-gray-400">
                          Agrega líneas al presupuesto
                        </TableCell>
                      </TableRow>
                    ) : form.lineas.map((linea, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {form.tipo === 'GASTOS' ? (
                            <TreeBudgetCategorySelect
                              categoriaId={linea.categoria_id}
                              centroCostoId={linea.centro_costo_id}
                              onValueChange={(values) => actualizarLinea(idx, values)}
                              categorias={categorias}
                              centrosCosto={centrosCosto}
                              placeholder="Seleccionar..."
                            />
                          ) : (
                            <Select 
                              value={linea.tienda_name ? `t-${linea.tienda_name}` : linea.vendedor_name ? `v-${linea.vendedor_name}` : undefined}
                              onValueChange={(v) => {
                                if (v.startsWith('t-')) {
                                  actualizarLinea(idx, { tienda_name: v.replace('t-', ''), vendedor_name: '' });
                                } else if (v.startsWith('v-')) {
                                  actualizarLinea(idx, { vendedor_name: v.replace('v-', ''), tienda_name: '' });
                                }
                              }}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Seleccionar...">
                                  {linea.tienda_name || linea.vendedor_name || null}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="header-t" disabled>-- Tiendas --</SelectItem>
                                {tiendas.map(t => <SelectItem key={`t-${t}`} value={`t-${t}`}>{t}</SelectItem>)}
                                <SelectItem value="header-v" disabled>-- Vendedores --</SelectItem>
                                {vendedores.map(v => <SelectItem key={`v-${v}`} value={`v-${v}`}>{v}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input value={linea.descripcion} onChange={(e) => actualizarLinea(idx, 'descripcion', e.target.value)} placeholder="Descripción" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={linea.monto_presupuestado} onChange={(e) => actualizarLinea(idx, 'monto_presupuestado', e.target.value)} className="text-right w-28" />
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => eliminarLinea(idx)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {form.lineas.length > 0 && (
                      <TableRow className="bg-gray-50">
                        <TableCell colSpan={2} className="text-right font-semibold">Total:</TableCell>
                        <TableCell className="text-right font-mono font-bold text-indigo-600">S/. {formatMoney(totalLineas)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea value={form.notas} onChange={(e) => setForm({...form, notas: e.target.value})} placeholder="Notas adicionales..." className="mt-1" />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={guardarPresupuesto} className="bg-indigo-600 hover:bg-indigo-700">
              {editando ? 'Guardar Cambios' : 'Crear Presupuesto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalle */}
      <Dialog open={detalleOpen} onOpenChange={setDetalleOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Ejecución de Presupuesto
            </DialogTitle>
          </DialogHeader>

          {presupuestoDetalle && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className={presupuestoDetalle.excedido ? 'border-red-300 bg-red-50' : presupuestoDetalle.en_alerta ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Ejecución</p>
                    <p className={`text-3xl font-bold ${presupuestoDetalle.excedido ? 'text-red-600' : presupuestoDetalle.en_alerta ? 'text-amber-600' : 'text-green-600'}`}>
                      {presupuestoDetalle.porcentaje_ejecucion}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Presupuestado</p>
                    <p className="text-2xl font-bold text-gray-700">S/. {formatMoney(presupuestoDetalle.monto_total)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Ejecutado</p>
                    <p className="text-2xl font-bold text-indigo-600">S/. {formatMoney(presupuestoDetalle.monto_ejecutado)}</p>
                  </CardContent>
                </Card>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Concepto</TableHead>
                    <TableHead className="text-right">Presupuestado</TableHead>
                    <TableHead className="text-right">Ejecutado</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead>Progreso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {presupuestoDetalle.lineas?.map((l, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {l.categoria_nombre || l.centro_costo_nombre || l.tienda_name || l.vendedor_name || l.descripcion}
                      </TableCell>
                      <TableCell className="text-right font-mono">S/. {formatMoney(l.monto_presupuestado)}</TableCell>
                      <TableCell className="text-right font-mono">S/. {formatMoney(l.monto_ejecutado)}</TableCell>
                      <TableCell className={`text-right font-bold ${l.porcentaje > 100 ? 'text-red-600' : l.porcentaje >= presupuestoDetalle.alerta_porcentaje ? 'text-amber-600' : 'text-green-600'}`}>
                        {l.porcentaje}%
                      </TableCell>
                      <TableCell className="w-32">
                        <Progress value={Math.min(l.porcentaje, 100)} className={`h-2 ${l.porcentaje > 100 ? '[&>div]:bg-red-500' : ''}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalleOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el presupuesto "{deleteConfirm?.nombre}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarPresupuesto} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PresupuestosPage;
